import { memo, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GradientText } from "lola-framework-ui-test";
import { ArrowDownIcon } from "../assets/icons";
import { BodyCopy } from "./BodyCopy";
import { ElevatedCircleWrapper } from "./ElevatedCircleWrapper";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import type { IFundingAttempt } from "../interfaces/cash-deposit";
import { accentInkOnSurface } from "../utils/accentInkOnSurface";
import { playAmountMismatchAlert } from "../utils/playAmountMismatchAlert";
import "./AmountMismatchTopDrawer.css";

export interface AmountMismatchTopDrawerProps {
  attempt: IFundingAttempt;
  onDismiss: () => void;
}

/**
 * Diameter of the floating dismiss mark. Single source for both the lola
 * `ElevatedCircle` (inline min-size) and the dialog CSS (`--mismatch-mark-size`
 * drives the bottom padding, the mark box and the inner icon disc).
 */
const MARK_SIZE_PX = 96;

function themeAccent(value: string): string {
  const hex = value.match(/#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})\b/);
  return hex?.[0] ?? value;
}

/**
 * Floating notice on the barcode sheet: the cashier typed a different amount.
 * Renders inside the bottom-aligned barcode popup and sits on the header so
 * the ElevatedCircle + down arrow points at the amount.
 */
export const AmountMismatchTopDrawer = memo(
  ({ onDismiss }: AmountMismatchTopDrawerProps) => {
    const { t } = useTranslation();
    const { theme } = useStore();
    const colorConfig = theme.whiteView;
    const {
      background,
      bodyCopy,
      title,
      backgroundIcon,
      backgroundBtn,
      textColorBtn,
    } = colorConfig;
    const partnerColors = useSessionStore(
      (s) =>
        s.session.data?.partner?.theme?.colors as
          | Record<string, string>
          | undefined,
    );
    const accentSolid = accentInkOnSurface(partnerColors, background, bodyCopy);

    useEffect(() => {
      playAmountMismatchAlert();
    }, []);

    const themeVars = useMemo(
      () =>
        ({
          "--mismatch-surface": background,
          "--mismatch-title": accentSolid,
          "--mismatch-accent": themeAccent(backgroundBtn),
          "--mismatch-mark-size": `${MARK_SIZE_PX}px`,
        }) as React.CSSProperties,
      [background, accentSolid, backgroundBtn],
    );

    return (
      <aside
        className="amount-mismatch-dialog"
        style={themeVars}
        role="alert"
        aria-live="assertive"
      >
        <GradientText
          as="h2"
          textColor={title}
          className="amount-mismatch-dialog__title"
        >
          {t("barcode.amountMismatch.title")}
        </GradientText>
        <BodyCopy
          textColor={bodyCopy}
          className="amount-mismatch-dialog__body"
        >
          {t("barcode.amountMismatch.body")}
        </BodyCopy>
        <button
          type="button"
          className="amount-mismatch-dialog__mark"
          onClick={onDismiss}
          aria-label={t("barcode.amountMismatch.dismiss")}
        >
          <ElevatedCircleWrapper
            size={MARK_SIZE_PX}
            circleContainerBg={backgroundIcon ?? background}
            iconContainerBg={backgroundBtn}
          >
            <ArrowDownIcon size={26} colors={[textColorBtn, textColorBtn]} />
          </ElevatedCircleWrapper>
        </button>
      </aside>
    );
  },
);
